import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useEditor, SELECT_LAYER, SET_PLAYHEAD, UPDATE_PROPERTY, UPDATE_KEYFRAME } from '@/lib/kinema';
import type { BaseLayerData, Editor, EditorState } from '@/lib/kinema';
import type { AdToken } from '@/shared/adTokens/adTokens';
import {
  adTokenNotes,
  containsAdTokenSyntax,
  replaceAliasWithCanonical,
  scanAdTokens,
  type AdTokenKind,
  type AdTokenNote,
} from '@/shared/adTokens/adTokenScan';
import { adTokenSeverity } from '@/shared/adTokens/adTokenSeverity';
import { useAdTokens, type AdTokenStatus } from '@/shared/adTokens/useAdTokens';

/**
 * The ad token check across a whole Create Studio composition (MPN-1933).
 *
 * The same check ad copy gets (MPN-1930), run over every text layer's text, so the
 * header can say how many warnings there are and the drawer can list them all -- not just
 * the ones on the layer that happens to be selected.
 *
 * Advisory only, like ad copy: nothing here blocks a save.
 */

/** One run of text on a layer that is checked: a text layer's text, or one keyframe's value of it. */
export type AdTokenSource = { field: 'text'; keyframeTime: number | null; text: string };

/**
 * Where a layer's checked ad tokens are: a text layer's text as the composition will show it.
 * A keyframed text layer shows its keyframe values, not its base text, so those are what is
 * checked. A layer's name is not checked -- it never renders (MPN-2068); see `inName`.
 */
export function adTokenSources(layer: BaseLayerData): AdTokenSource[] {
  const sources: AdTokenSource[] = [];
  if (layer.type !== 'text') return sources;
  const effect = layer.effects?.text;
  if (effect?.enabled && effect.keyframes.length > 0) {
    for (const kf of effect.keyframes) sources.push({ field: 'text', keyframeTime: kf.time, text: String(kf.value ?? '') });
  } else {
    sources.push({ field: 'text', keyframeTime: null, text: String(layer.text ?? '') });
  }
  return sources;
}

/** A note on one layer, with where its token first appears so a jump can land on it. */
export type LayerAdTokenNote = AdTokenNote & {
  field: AdTokenSource['field'];
  keyframeTime: number | null;
  start: number;
  end: number;
};

export type LayerAdTokenFindings = {
  /** One per token per source, in the order they appear. */
  notes: LayerAdTokenNote[];
  /**
   * The most serious kind of token in the layer's text, `known` included -- the layer list's
   * tint. Null when only the name carries a token.
   */
  worst: AdTokenKind | null;
  /** The same, per keyframe, for a keyframed layer. */
  keyframeKinds: Map<number, AdTokenKind>;
  /**
   * A token in the layer's name. Not a finding -- no note, not counted -- only the layer
   * list's name marker (MPN-2068). Not the composition's own name, which is the project's.
   */
  inName: boolean;
};

const SEVERITY: AdTokenKind[] = ['known', 'alias', 'instruction', 'unknown'];
const worse = (a: AdTokenKind, b: AdTokenKind) => (SEVERITY.indexOf(b) > SEVERITY.indexOf(a) ? b : a);

/** What the check finds on one layer, or null if neither its text nor its name has an ad token. */
export function layerAdTokenFindings(layer: BaseLayerData, tokens: AdToken[]): LayerAdTokenFindings | null {
  const notes: LayerAdTokenNote[] = [];
  const keyframeKinds = new Map<number, AdTokenKind>();
  let worst: AdTokenKind | null = null;
  for (const source of adTokenSources(layer)) {
    const ranges = scanAdTokens(source.text, tokens);
    if (ranges.length === 0) continue;
    const sourceWorst = ranges.reduce<AdTokenKind>((w, r) => worse(w, r.kind), 'known');
    worst = worst ? worse(worst, sourceWorst) : sourceWorst;
    if (source.keyframeTime !== null) keyframeKinds.set(source.keyframeTime, sourceWorst);
    for (const note of adTokenNotes(ranges)) {
      const first = ranges.find((r) => r.normalized === note.normalized)!;
      notes.push({ ...note, field: source.field, keyframeTime: source.keyframeTime, start: first.start, end: first.end });
    }
  }
  const inName = layer.type !== 'composition' && scanAdTokens(String(layer.name ?? ''), tokens).length > 0;
  return worst || inName ? { notes, worst, keyframeKinds, inName } : null;
}

/** Whether a note counts toward the header's warning count. adTokenSeverity decides. */
export function isAdTokenWarning(note: Pick<AdTokenNote, 'kind'>): boolean {
  return adTokenSeverity(note.kind) === 'warning';
}

/**
 * Rewrite an alias to its canonical as one undoable command: the base text through
 * UPDATE_PROPERTY, a keyframe's value through UPDATE_KEYFRAME at that keyframe's time. Reads
 * the layer as it is now rather than as the note saw it.
 */
export function fixAdTokenAlias(
  editor: Editor,
  tokens: AdToken[],
  layerId: string,
  note: Pick<LayerAdTokenNote, 'field' | 'keyframeTime' | 'normalized'>,
): void {
  const layer = editor.getState().layers[layerId];
  if (!layer) return;
  if (note.keyframeTime === null) {
    const next = replaceAliasWithCanonical(String(layer[note.field] ?? ''), tokens, note.normalized);
    if (next) editor.dispatchCommand(UPDATE_PROPERTY, { layerId, property: note.field, value: next.value });
    return;
  }
  const kf = layer.effects.text?.keyframes.find((k) => k.time === note.keyframeTime);
  if (!kf) return;
  const next = replaceAliasWithCanonical(String(kf.value ?? ''), tokens, note.normalized);
  if (next) editor.dispatchCommand(UPDATE_KEYFRAME, { layerId, property: 'text', time: kf.time, value: next.value });
}

// ─── Composition-wide findings ────────────────────────────

/** Findings per layer object, per vocabulary. A layer is replaced on every edit, so an unchanged one is a hit. */
const cache = new WeakMap<AdToken[], WeakMap<BaseLayerData, LayerAdTokenFindings | null>>();

function findingsFor(layer: BaseLayerData, tokens: AdToken[]): LayerAdTokenFindings | null {
  let byLayer = cache.get(tokens);
  if (!byLayer) cache.set(tokens, (byLayer = new WeakMap()));
  if (!byLayer.has(layer)) byLayer.set(layer, layerAdTokenFindings(layer, tokens));
  return byLayer.get(layer) ?? null;
}

type ByLayer = Record<string, LayerAdTokenFindings>;

/** The findings for every layer, or `previous` itself if none of them changed. */
function findingsByLayer(state: EditorState, tokens: AdToken[], previous: ByLayer): ByLayer {
  const next: ByLayer = {};
  let changed = false;
  for (const layer of Object.values(state.layers)) {
    const findings = findingsFor(layer, tokens);
    if (!findings) continue;
    next[layer.id] = findings;
    if (previous[layer.id] !== findings) changed = true;
  }
  return changed || Object.keys(next).length !== Object.keys(previous).length ? next : previous;
}

/** Whether any layer has a note: an alias, instruction or unknown token. A name-only token has none (MPN-2068). */
export function hasNotes(byLayer: ByLayer): boolean {
  return Object.values(byLayer).some((f) => f.notes.length > 0);
}

function hasTokenSyntax(state: EditorState): boolean {
  return Object.values(state.layers).some((layer) =>
    adTokenSources(layer).some((source) => containsAdTokenSyntax(source.text)));
}

/** A place in a layer's text for the Properties panel to focus once the jump has rendered. */
export type AdTokenFocusRequest = { layerId: string; field: AdTokenSource['field']; start: number; end: number };

export type AdTokenFindingsValue = {
  /** Whether the Studio checks ad tokens at all (MPN-2014). Off outside a provider. */
  enabled: boolean;
  status: AdTokenStatus;
  /** The vocabulary, for the picker. Empty unless ready. */
  tokens: AdToken[];
  byLayer: ByLayer;
  warningCount: number;
  /** Something token-shaped in the composition and no vocabulary to check it against. */
  unavailable: boolean;
  drawerOpen: boolean;
  setDrawerOpen: (open: boolean | ((open: boolean) => boolean)) => void;
  /** Select the layer, move to the keyframe, and ask for its token to be focused. */
  jumpTo: (layerId: string, note: LayerAdTokenNote) => void;
  focusRequest: AdTokenFocusRequest | null;
  clearFocusRequest: () => void;
};

const EMPTY: ByLayer = {};
const EMPTY_TOKENS: AdToken[] = [];

const AdTokenFindingsContext = createContext<AdTokenFindingsValue>({
  enabled: false,
  status: 'loading',
  tokens: [],
  byLayer: EMPTY,
  warningCount: 0,
  unavailable: false,
  drawerOpen: false,
  setDrawerOpen: () => {},
  jumpTo: () => {},
  focusRequest: null,
  clearFocusRequest: () => {},
});

/** `enabled` false reads no vocabulary, so the check stays loading and finds nothing (MPN-2014). */
export function AdTokenFindingsProvider({ enabled = true, children }: { enabled?: boolean; children: ReactNode }) {
  const editor = useEditor();
  const { tokens: vocabulary, status } = useAdTokens(enabled);
  const tokens = status === 'ready' ? vocabulary : EMPTY_TOKENS;
  const [byLayer, setByLayer] = useState<ByLayer>(EMPTY);
  const [unavailable, setUnavailable] = useState(false);
  const [drawerOpen, setDrawerOpenState] = useState(false);
  // Set once the drawer has opened itself, or someone has opened or closed it.
  const drawerSettled = useRef(false);
  const [focusRequest, setFocusRequest] = useState<AdTokenFocusRequest | null>(null);

  useEffect(() => {
    const update = (state: EditorState) => {
      setByLayer((previous) => (status === 'ready' ? findingsByLayer(state, tokens, previous) : EMPTY));
      setUnavailable(status === 'unavailable' && hasTokenSyntax(state));
    };
    update(editor.getState());
    // A composition that opens with notes opens with the drawer open, so they are not missed
    // (MPN-2069). Once per mount -- one mount is one composition open -- on the first check
    // there is a vocabulary for, and never after a toggle: closing it, then typing a token,
    // leaves it closed.
    if (status === 'ready' && !drawerSettled.current) {
      drawerSettled.current = true;
      if (hasNotes(findingsByLayer(editor.getState(), tokens, EMPTY))) setDrawerOpenState(true);
    }
    return editor.subscribe(update);
  }, [editor, tokens, status]);

  const setDrawerOpen = useCallback((open: boolean | ((open: boolean) => boolean)) => {
    drawerSettled.current = true;
    setDrawerOpenState(open);
  }, []);

  const warningCount = useMemo(
    () => Object.values(byLayer).reduce((n, f) => n + f.notes.filter(isAdTokenWarning).length, 0),
    [byLayer],
  );

  const jumpTo = useCallback((layerId: string, note: LayerAdTokenNote) => {
    const layer = editor.getState().layers[layerId];
    if (!layer) return;
    editor.batch(() => {
      editor.dispatchCommand(SELECT_LAYER, { layerId });
      if (note.keyframeTime !== null) {
        editor.dispatchCommand(SET_PLAYHEAD, { time: layer.startTime + note.keyframeTime });
      }
    });
    setFocusRequest({ layerId, field: note.field, start: note.start, end: note.end });
  }, [editor]);

  const clearFocusRequest = useCallback(() => setFocusRequest(null), []);

  // A request is for the render the jump caused. The Text input consumes it in that render's
  // effects; one nothing consumed (no input on screen) is dropped, so it cannot steal focus
  // later when a layer happens to be selected.
  useEffect(() => {
    if (!focusRequest) return;
    const timer = setTimeout(() => setFocusRequest(null), 0);
    return () => clearTimeout(timer);
  }, [focusRequest]);

  const value = useMemo(() => ({
    enabled, status, tokens, byLayer, warningCount, unavailable,
    drawerOpen, setDrawerOpen, jumpTo, focusRequest, clearFocusRequest,
  }), [enabled, status, tokens, byLayer, warningCount, unavailable, drawerOpen, jumpTo, focusRequest, clearFocusRequest]);

  return <AdTokenFindingsContext.Provider value={value}>{children}</AdTokenFindingsContext.Provider>;
}


export const useAdTokenFindings = () => useContext(AdTokenFindingsContext);
